import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { Alert, Button, Col, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import moment from "moment";
import * as Yup from "yup";
import Card from "src/components/Cards/Basic";
import FormInputText from "src/components/Forms/FormInputText";
import FormSelectBox from "src/components/Forms/FormSelectBox";
import ReservationChildrenFields from "src/components/Forms/ReservationChildrenFields";
import GeneralFormError from "src/components/Forms/GeneralFormError";
import BackButton from "src/components/UiComponents/Buttons/BackButton";
import ConfirmDialog from "src/components/Modal/ConfirmDialog";
import { showSuccessToast } from "src/components/Toasts";
import axios from "src/config/axios";
import history from "src/global/history";
import { linkBuilder } from "src/global/navigationHelper";
import { keepState } from "src/components/UiComponents/Buttons/BackButton/helper";
import { localeOptions } from "src/global";
import { DashboardContext } from "src/routes/DashboardRoutes";
import ReservationPricePreview from "./ReservationPricePreview";

function reservationAttributes(values) {
  return {
    from: `${values.from_date}T${values.from_time}`,
    to: `${values.to_date}T${values.to_time}`,
    parking_slot_id: values.parking_slot_id || null,
    amount_guests: values.amount_guests,
    reservation_child_ages: values.reservation_child_ages,
    amount_dogs: values.amount_dogs,
    contact_first_name: values.contact_first_name,
    contact_last_name: values.contact_last_name,
    contact_email: values.contact_email,
    contact_phone: values.contact_phone,
    language: values.language,
  };
}

const EditOnlineReservationPage = () => {
  const { id } = useParams();
  const { settings, configuration, locales, default_locale } =
    useContext(DashboardContext);
  const business = settings.business;
  const [reservation, setReservation] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [parkingSlots, setParkingSlots] = useState([]);
  const [parkingSlotsLoading, setParkingSlotsLoading] = useState(false);
  const [showOverlapDialog, setShowOverlapDialog] = useState(false);
  const formRef = useRef();
  const saving = useRef(false);
  const startedAt = useRef(moment());

  useEffect(() => {
    setReservation(null);
    setLoadError(null);
    if (!id) return;
    let current = true;
    axios
      .get(`/api/v1/online_reservations/${id}.json`)
      .then(({ data }) => {
        if (current) setReservation(data);
      })
      .catch(() => {
        if (current) setLoadError("Die Buchung konnte nicht geladen werden.");
      });
    return () => {
      current = false;
    };
  }, [id]);

  function initialValues() {
    const from = moment(reservation?.from || startedAt.current);
    const to = moment(
      reservation?.to || moment(startedAt.current).add(1, "day")
    );
    return {
      from_date: from.format(moment.HTML5_FMT.DATE),
      from_time:
        !id && business.booking_period_type === "default"
          ? business.checkin_time
          : from.format(moment.HTML5_FMT.TIME),
      to_date: to.format(moment.HTML5_FMT.DATE),
      to_time:
        !id && business.booking_period_type === "default"
          ? business.checkout_time
          : to.format(moment.HTML5_FMT.TIME),
      parking_slot_id: reservation?.parking_slot_id || "",
      amount_guests: reservation?.amount_guests ?? 1,
      reservation_child_ages: reservation?.reservation_child_ages || [],
      amount_dogs: reservation?.amount_dogs ?? 0,
      contact_first_name: reservation?.contact_first_name || "",
      contact_last_name: reservation?.contact_last_name || "",
      contact_email: reservation?.contact_email || "",
      contact_phone: reservation?.contact_phone || "",
      language: reservation?.language || default_locale || locales[0] || "de",
    };
  }

  const validationSchema = Yup.object({
    from_date: Yup.string().required("Bitte ein Anreisedatum eingeben."),
    from_time: Yup.string().required("Bitte eine Anreisezeit eingeben."),
    to_date: Yup.string().required("Bitte ein Abreisedatum eingeben."),
    to_time: Yup.string().required("Bitte eine Abreisezeit eingeben."),
    parking_slot_id: business.parking_slots_enabled
      ? Yup.number().typeError("Bitte einen Stellplatz auswählen.").required()
      : Yup.mixed().nullable(),
    amount_guests: Yup.number().required(),
    amount_dogs: Yup.number().required(),
    reservation_child_ages: Yup.array().of(
      Yup.number().required("Bitte ein Alter angeben.")
    ),
    contact_first_name: Yup.string()
      .trim()
      .required("Bitte den Vornamen eingeben."),
    contact_last_name: Yup.string()
      .trim()
      .required("Bitte den Nachnamen eingeben."),
    contact_email: Yup.string().email(
      "Bitte eine gültige E-Mail-Adresse eingeben."
    ),
  });

  async function reloadParkingSlots(values) {
    setParkingSlotsLoading(true);
    try {
      const attributes = reservationAttributes(values);
      const { data } = await axios.get("/api/v1/parking_slots.json", {
        params: {
          from: attributes.from,
          to: attributes.to,
          active: true,
          ignore_occupancy_of_booking_id: id,
        },
      });
      setParkingSlots(
        data.entries.map((slot) => ({
          ...slot,
          name: `${slot.name} (${slot.is_available ? "Verfügbar" : "Besetzt"})`,
        }))
      );
    } catch (error) {
      formRef.current.setStatus({
        base: "Die Stellplätze konnten nicht geladen werden.",
      });
    } finally {
      setParkingSlotsLoading(false);
    }
  }

  async function submit(values, helpers, allowOverlap = false) {
    if (saving.current) return;
    saving.current = true;
    helpers.setSubmitting(true);
    helpers.setStatus(null);
    try {
      const payload = {
        online_reservation: {
          ...reservationAttributes(values),
          allow_overlap: allowOverlap,
        },
      };
      const { data } = id
        ? await axios.put(`/api/v1/online_reservations/${id}.json`, payload)
        : await axios.post("/api/v1/online_reservations.json", payload);
      history.push(
        linkBuilder(`/dashboard/stays/${data.stay_id}`),
        keepState(history)
      );
      showSuccessToast();
    } catch (error) {
      const errors = error.response?.data;
      if (errors?.parking_slot_overlap) {
        setShowOverlapDialog(true);
      } else {
        helpers.setStatus(
          errors || { base: "Die Buchung konnte nicht gespeichert werden." }
        );
        if (errors)
          helpers.setErrors({
            ...errors,
            from_date: errors.from,
            to_date: errors.to,
          });
      }
    } finally {
      saving.current = false;
      helpers.setSubmitting(false);
    }
  }

  function confirmOverlap() {
    setShowOverlapDialog(false);
    submit(formRef.current.values, formRef.current, true);
  }

  if (loadError) return <Alert variant="danger">{loadError}</Alert>;
  if (id && !reservation) return <Spinner animation="border" />;

  const editable = !id || reservation.state.startsWith("reservation_");
  const slotOptions = parkingSlots.length
    ? parkingSlots
    : reservation?.parking_slot
      ? [reservation.parking_slot]
      : [];

  return (
    <Card
      title={
        <div className="d-flex align-items-center">
          <BackButton
            fallbackRoute={
              id
                ? `/dashboard/stays/${reservation.stay_id}`
                : "/dashboard/bookings"
            }
          />
          <p className="mb-0">{id ? "Buchung bearbeiten" : "Neue Buchung"}</p>
        </div>
      }
    >
      {!editable ? (
        <Alert variant="info">
          Diese Buchung wurde bereits eingecheckt. Bitte die Buchungsdaten im
          Aufenthalt bearbeiten.
        </Alert>
      ) : (
        <Formik
          initialValues={initialValues()}
          enableReinitialize
          innerRef={formRef}
          validationSchema={validationSchema}
          onSubmit={submit}
        >
          {({ values, status, isSubmitting }) => (
            <Form>
              <h3>Zeitraum</h3>
              <Row>
                <Col sm={6}>
                  <FormInputText
                    name="from_date"
                    label="Anreise"
                    type="date"
                    required
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="from_time"
                    label="Anreisezeit"
                    type="time"
                    required
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="to_date"
                    label="Abreise"
                    type="date"
                    required
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="to_time"
                    label="Abreisezeit"
                    type="time"
                    required
                  />
                </Col>
              </Row>
              {business.parking_slots_enabled && (
                <FormSelectBox
                  name="parking_slot_id"
                  label="Stellplatz"
                  options={slotOptions}
                  option_value="id"
                  loading={parkingSlotsLoading}
                  onClick={() => reloadParkingSlots(values)}
                  required
                />
              )}
              <h3>Gäste</h3>
              <Row>
                <Col sm={6}>
                  <FormSelectBox
                    name="amount_guests"
                    label="Anzahl Erwachsene (18+ Jahre)"
                    options={Array.from(
                      {
                        length: Math.max(
                          6,
                          (Number(values.amount_guests) || 0) + 1
                        ),
                      },
                      (_, index) => ({ value: index + 1, name: index + 1 })
                    )}
                    required
                  />
                </Col>
                {business.dogs_enabled && (
                  <Col sm={6}>
                    <FormSelectBox
                      name="amount_dogs"
                      label="Anzahl Hunde"
                      options={Array.from(
                        {
                          length: Math.max(
                            5,
                            (Number(values.amount_dogs) || 0) + 2
                          ),
                        },
                        (_, count) => ({ value: count, name: count })
                      )}
                    />
                  </Col>
                )}
              </Row>
              <ReservationChildrenFields />
              <h3>Kontaktdaten</h3>
              <Row>
                <Col sm={6}>
                  <FormInputText
                    name="contact_first_name"
                    label="Vorname"
                    required
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="contact_last_name"
                    label="Nachname"
                    required
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="contact_email"
                    label="E-Mail (optional)"
                    type="email"
                  />
                </Col>
                <Col sm={6}>
                  <FormInputText
                    name="contact_phone"
                    label="Telefonnummer (optional)"
                    type="tel"
                  />
                </Col>
              </Row>
              <FormSelectBox
                name="language"
                label="Sprache"
                options={
                  locales.length
                    ? localeOptions(configuration, locales)
                    : configuration.available_locales
                }
                option_value="locale"
              />
              <ReservationPricePreview
                attributes={reservationAttributes(values)}
                bookingId={id}
              />
              <GeneralFormError errors={status} />
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Spinner animation="border" size="sm" className="mr-2" />
                )}
                {id ? "Änderungen speichern" : "Buchung anlegen"}
              </Button>
              {showOverlapDialog && (
                <ConfirmDialog
                  title="Stellplatz bereits belegt"
                  content="Der Stellplatz ist im gewählten Zeitraum bereits belegt. Möchten Sie die Überschneidung trotzdem speichern?"
                  handleClose={() => setShowOverlapDialog(false)}
                  handleSubmit={confirmOverlap}
                  confirmBtnText="Trotzdem speichern"
                />
              )}
            </Form>
          )}
        </Formik>
      )}
    </Card>
  );
};

export default EditOnlineReservationPage;
